"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import { useMenu } from "@/lib/menu/MenuProvider";
import { MenuItem } from "@/lib/menu/MenuItem";
import { copyText } from "@/lib/clipboard";
import { menus, type MenuDef } from "@/lib/menu/menus";
import { hideTip, showTip } from "@/lib/menu/Tooltip";
import type { ChatKind } from "./ChatShell";
import {
  channelAt,
  MEMBERS,
  SERVER,
  THREADS,
  type ChatMsg,
  type EmbedData,
  type Member,
  type Status,
} from "./data";
import { MemberBadges, profileDef } from "./DetailsSidebar";
import { EmojiPicker, emojiDef } from "./EmojiPicker";
import { EMOJIS, Emoji, type EmojiRow } from "./emoji";
import { mdRender } from "./md";
import { reportDef } from "@/lib/menu/dialogs";
import { DashboardView } from "./DashboardView";
import { FriendsView } from "./FriendsView";
import {
  ArrowLeftIcon,
  CameraIcon,
  CheckIcon,
  ChevronDownIcon,
  DotsIcon,
  EmbedIcon,
  FileIcon,
  FlagIcon,
  HashIcon,
  InfoIcon,
  KeyboardIcon,
  MicIcon,
  PencilIcon,
  PinIcon,
  PlusIcon,
  RefreshIcon,
  ReplyIcon,
  SearchIcon,
  GifIcon,
  SendIcon,
  SidebarIcon,
  SmileIcon,
  StickerIcon,
  TrashIcon,
  XIcon,
} from "./icons";
import { Pfp } from "./Pfp";
import { IconButton } from "./ui";

const CAP = 1700;
const AUDIO_RE = /\.(ogg|mp3|wav|m4a|opus|flac)$/i;
const VIDEO_RE = /\.(mp4|mov|mkv|avi)$/i;
const IMAGE_RE = /\.(png|jpe?g|gif|webp|avif|bmp|svg)$/i;
type AttachKind = "image" | "audio" | "video" | "file";
/* explicit kind wins; then extension; then a src implies image (the
   picker only sets src on image/*) */
const attachKind = (f: { name: string; src?: string; kind?: string }): AttachKind =>
  f.kind === "audio" || f.kind === "video" || f.kind === "image"
    ? f.kind
    : VIDEO_RE.test(f.name)
      ? "video"
      : AUDIO_RE.test(f.name) || /\.webm$/i.test(f.name)
        ? "audio"
        : IMAGE_RE.test(f.name) || f.src
          ? "image"
          : "file";

type SpellMiss = {
  word: string;
  off: number;
  len: number;
  sug: string[];
  fix?: string;
};

/* the local account rendered as a thread author — no Member record,
   so profile clicks/badges no-op on it */
const YOU_AUTHOR = {
  name: "username",
  src: "/pfp.png",
  status: "online" as Status,
  m: undefined as Member | undefined,
};

function memberOf(author: string) {
  if (author === "you") return YOU_AUTHOR;
  const m = MEMBERS.find((x) => x.id === author);
  return m ? { name: m.name, src: m.src, status: m.status, m } : YOU_AUTHOR;
}

/* scroll-up pagination — history is simulated until the backend lands:
   each pull prepends a deterministic page of older messages */
const MAX_PAGES = 3;
const PAST_LINES = [
  "back when this all started",
  "i cant believe we used to ship like this",
  "famous last words, archived",
  "`git log --oneline` tells a story",
  "the vibes were immaculate that week",
  "someone screenshot this for the museum",
  "day one of asking for dark mode",
  "we really said 'temporary fix' huh",
  "**historic** message, handle with care",
  "the incident of june 13th was foretold here",
  "bookmarking this for the postmortem",
  "ok but did anyone document it",
];
function olderMsgs(key: string, page: number, dm?: Member): ChatMsg[] {
  /* dm history only ever involves the two people in it; notes is
     solo — only the gc/server channels get the full roster */
  const authors = dm
    ? [dm.id, "you"]
    : key === "notes"
      ? ["you"]
      : MEMBERS.map((x) => x.id);
  return Array.from({ length: 15 }, (_, i) => ({
    id: `old-${key}-${page}-${i}`,
    author: authors[(key.length + page * 3 + i) % authors.length],
    t: `${7 - page}:${String(50 - ((i * 4) % 50)).padStart(2, "0")} AM`,
    text: PAST_LINES[(page * 5 + i) % PAST_LINES.length],
  }));
}

/* chat header identity per route — real names, no skeletons */
const HEAD: Record<
  ChatKind,
  { src?: string; status?: Status; title: string; sub: string }
> = {
  dm: { src: "/dm1.jpg", status: "online", title: "Milk", sub: "milk#0001" },
  gc: { src: "/gc.jpg", title: "The GC", sub: "10 members" },
  notes: { src: "/notes.jpg", title: "Notes", sub: "10 notes" },
  /* server fallback — a real channel resolves off the path below */
  server: { title: SERVER.name, sub: "" },
  /* dashboard/friends render their own views — never read */
  dashboard: { title: "Dashboard", sub: "" },
  friends: { title: "Friends", sub: "" },
};

/* composer placeholder per route */
const MESSAGE_TO: Record<ChatKind, string> = {
  dm: "Milk",
  gc: "The GC",
  notes: "Notes",
  server: "a channel",
  dashboard: "the dashboard",
  friends: "friends",
};

export function MainFrame({
  mobile = false,
  onToggleDetails,
  detailsOn = false,
  kind = "dm",
}: {
  mobile?: boolean;
  /* toggles the details sidebar — inline collapse on xl+, pullout overlay
     on smaller desktop, bottom sheet on mobile */
  onToggleDetails: () => void;
  detailsOn?: boolean;
  kind?: ChatKind;
}) {
  const menu = useMenu();
  const pathname = usePathname();
  /* dm routes carry the member id — /app/d/gravel shows Gravel, not a
     hardcoded header */
  const dm = kind === "dm"
    ? MEMBERS.find((m) => pathname === `/app/d/${m.id}`)
    : undefined;
  /* server routes carry the channel id — /s/<server>/<channel> shows
     "# general"-style heads, no pfp */
  const chan = kind === "server" ? channelAt(pathname) : undefined;
  const head = dm
    ? { src: dm.src, status: dm.status, title: dm.name, sub: `${dm.id}#${dm.tag}` }
    : chan
      ? { title: chan.name, sub: chan.topic }
      : HEAD[kind];

  const [draft, setDraft] = useState("");
  const textarea = useRef<HTMLTextAreaElement>(null);
  /* spell.check — misses drive the wavy underlines in the mirror; it must
     keep pixel-exact metrics with the textarea (same font/leading/padding/
     wrap) so offsets line up */
  const [misses, setMisses] = useState<SpellMiss[]>([]);
  const spellMirror = useRef<HTMLDivElement>(null);
  /* :shortcode: autocomplete — {start,end} is the `:query` span in the
     draft so accept() can splice the char in exactly there */
  const [ac, setAc] = useState<{
    list: EmojiRow[];
    sel: number;
    start: number;
    end: number;
  } | null>(null);

  /* watch the caret for a trailing :query — ≥2 chars, letters/_/-.
     runs on type AND caret moves (click, arrows) so closing/resuming
     a half-typed token re-opens the pop */
  function checkShortcode(v: string, pos: number) {
    const m = v.slice(0, pos).match(/:([a-z0-9_+\-]{2,})$/);
    if (!m) {
      setAc(null);
      return;
    }
    const q = m[1].toLowerCase();
    const list = EMOJIS.filter((r) => r.n.includes(q)).slice(0, 8);
    setAc(list.length ? { list, sel: 0, start: pos - m[0].length, end: pos } : null);
  }

  /* swap the `:query` token for the real char, caret after it */
  function acceptShortcode(r: EmojiRow) {
    if (!ac) return;
    const next = draft.slice(0, ac.start) + r.e + draft.slice(ac.end);
    const at = ac.start + r.e.length;
    setDraft(next);
    setAc(null);
    requestAnimationFrame(() => {
      const el = textarea.current;
      el?.focus();
      el?.setSelectionRange(at, at);
      grow();
    });
  }

  /* the thread — keyed by who's on screen; reseeds mid-render when the
     route changes (React's adjust-state-during-render pattern) */
  /* which message is being edited — up-arrow grabs the last own one */
  const [editing, setEditing] = useState<ChatMsg | null>(null);
  const threadKey = dm?.id ?? chan?.id ?? kind;
  const [msgs, setMsgs] = useState<ChatMsg[]>(() => THREADS[threadKey] ?? []);
  const [replyTo, setReplyTo] = useState<ChatMsg | null>(null);
  /* files staged on the composer — become attachments on send */
  const [pending, setPending] = useState<
    { name: string; src?: string; kind?: AttachKind }[]
  >(
    [],
  );
  const fileInput = useRef<HTMLInputElement>(null);
  /* how many history pages were pulled in — exhausts after MAX_PAGES */
  const [pages, setPages] = useState(0);
  const [loadingPast, setLoadingPast] = useState(false);
  const hasMore = pages < MAX_PAGES;
  /* scrollHeight snapshot taken right before a history prepend —
     restored in a layout effect so the viewport doesn't jump */
  const anchor = useRef<number | null>(null);
  /* true when the user's scrolled up — gates the jump-to-latest pill */
  const [scrolledUp, setScrolledUp] = useState(false);
  /* mobile media panel — emoji/sticker/gif picker docked UNDER the
     composer keyboard-style instead of a popup; it pushes the composer
     up and shrinks the thread */
  const [mPicker, setMPicker] = useState(false);
  /* reply-quote click → accent flash on the landed message */
  const [flash, setFlash] = useState<string | null>(null);
  const [prevKey, setPrevKey] = useState(threadKey);
  if (prevKey !== threadKey) {
    setPrevKey(threadKey);
    setMsgs(THREADS[threadKey] ?? []);
    setReplyTo(null);
    setDraft("");
    setEditing(null);
    setPending([]);
    setPages(0);
    setMPicker(false);
  }
  const threadRef = useRef<HTMLDivElement>(null);
  /* scroll to the newest message on thread load + every send — keyed on
     the LAST message's id so prepending history above doesn't yank
     the viewport back down */
  const lastId = msgs[msgs.length - 1]?.id;
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastId, threadKey]);
  /* restore the pre-prepend scroll offset — keeps the same message
     under the cursor when older history lands above */
  useLayoutEffect(() => {
    const el = threadRef.current;
    if (el && anchor.current != null) {
      el.scrollTop += el.scrollHeight - anchor.current;
      anchor.current = null;
    }
  }, [msgs]);

  /* the mobile picker pushes the composer up and shrinks the thread —
     a bottom-pinned view re-pins so the last message doesn't jump;
     scrolled-up readers keep their exact offset (the viewport just
     gets shorter under the same top edge) */
  useLayoutEffect(() => {
    const el = threadRef.current;
    if (el && !scrolledUp) el.scrollTop = el.scrollHeight;
  }, [mobile, mPicker, scrolledUp]);

  /* scrolled to the top → pull the next page of history (simulated —
     the real fetch lands with the backend) */
  function loadPast() {
    if (!hasMore || loadingPast) return;
    const el = threadRef.current;
    if (!el) return;
    setLoadingPast(true);
    anchor.current = el.scrollHeight;
    setTimeout(() => {
      setMsgs((ms) => [...olderMsgs(threadKey, pages, dm), ...ms]);
      setPages((p) => p + 1);
      setLoadingPast(false);
    }, 350);
  }

  /* @name resolver for the md renderer — a real member gets a profile
     pop on click; pinging the local account paints the amber "you were
     mentioned" highlight */
  const mention = useCallback(
    (name: string) => {
      const lower = name.toLowerCase();
      if (lower === "you" || lower === YOU_AUTHOR.name.toLowerCase())
        return { me: true };
      const m = MEMBERS.find((x) => x.name.toLowerCase() === lower);
      return m ? { click: () => menu.popup(profileDef(m)) } : undefined;
    },
    [menu],
  );

  /* scrolls the thread to a message + flashes it — the reply quote
     and pins popup click target */
  const jumpTo = useCallback((id: string) => {
    const el = threadRef.current?.querySelector(`[data-mid="${id}"]`);
    if (!(el instanceof HTMLElement)) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setFlash(id);
    setTimeout(() => setFlash((f) => (f === id ? null : f)), 1400);
  }, []);

  /* reactions — toggle the local account's pick on a message: existing
     chip flips `me` + bumps the count; new emoji opens a fresh chip */
  const toggleReaction = useCallback((id: string, e: string) => {
    setMsgs((ms) =>
      ms.map((m) => {
        if (m.id !== id) return m;
        const list = m.reactions ?? [];
        const i = list.findIndex((r) => r.e === e);
        if (i === -1)
          return { ...m, reactions: [...list, { e, n: 1, me: true }] };
        const r = list[i];
        const n = r.n + (r.me ? -1 : 1);
        /* un-reacting drops MY count — the chip only dies at 0, other
           people's reactions stay */
        const next =
          n <= 0
            ? list.filter((x) => x !== r)
            : [...list.slice(0, i), { ...r, n, me: !r.me }, ...list.slice(i + 1)];
        return { ...m, reactions: next };
      }),
    );
  }, []);

  /* typing indicator — a real typing feed lands with the backend; for
     now dm/gc threads show the other side typing on a lazy loop so
     the UI path is exercised */
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    if (kind !== "dm" && kind !== "gc") return;
    let show: ReturnType<typeof setTimeout>;
    const loop = setInterval(() => {
      setTyping(true);
      show = setTimeout(() => setTyping(false), 3200);
    }, 25000);
    const first = setTimeout(() => {
      setTyping(true);
      show = setTimeout(() => setTyping(false), 3200);
    }, 4000);
    return () => {
      clearInterval(loop);
      clearTimeout(first);
      clearTimeout(show);
    };
  }, [kind, threadKey]);

  /* desktop: typing anywhere drops straight into the composer, like
     Discord — printable keys only, and never steal focus from another
     editable/interactive element */
  useEffect(() => {
    if (mobile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
      const ae = document.activeElement;
      if (ae === textarea.current) return;
      if (
        ae instanceof HTMLElement &&
        ae !== document.body &&
        ae.closest(
          "input, textarea, select, [contenteditable], button, a, [role='button']",
        )
      )
        return;
      textarea.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobile]);

  /* Ctrl F → search popup, Ctrl D → toggle the details sidebar */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      const k = e.key.toLowerCase();
      if (k === "f") {
        e.preventDefault();
        menu.popup(menus.search);
      } else if (k === "d") {
        e.preventDefault();
        onToggleDetails();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menu, onToggleDetails]);

  /* measured textarea height — drives the side controls sliding from
     centered to bottom-aligned as extra lines are added */
  const [taH, setTaH] = useState(36);

  const overCap = draft.length > CAP;
  /* px the side controls drop — ONLY once a real second line exists
     (one line ≈ 40px stays dead-centered; line 2 pushes past 48px and
     the icons slide to the floor). clearing back to one line recenters */
  const drop = taH > 48 ? Math.max(0, taH / 2 - 18) : 0;

  /* embed mode — the whole composer slides down out of its slot while
     the form-builder bar slides up to replace it. the track holds both
     bars stacked; the window's height animates between whichever is
     active so the thread above only moves by the real delta */
  const [embedMode, setEmbedMode] = useState(false);
  const [embed, setEmbed] = useState<EmbedData>({});
  const [seg, setSeg] = useState({ header: true, body: true, footer: true });
  const coBar = useRef<HTMLDivElement>(null);
  const edBar = useRef<HTMLDivElement>(null);
  const [barH, setBarH] = useState({ co: 0, ed: 0 });
  /* keyed to kind — dashboard/friends early-return before the bars
     exist, so the observer must (re)attach once a chat view mounts */
  useEffect(() => {
    const sync = () =>
      setBarH({
        co: coBar.current?.offsetHeight ?? 0,
        ed: edBar.current?.offsetHeight ?? 0,
      });
    sync();
    const ro = new ResizeObserver(sync);
    if (coBar.current) ro.observe(coBar.current);
    if (edBar.current) ro.observe(edBar.current);
    return () => ro.disconnect();
  }, [kind]);

  function submitEmbed() {
    /* strip empties — a bare tone or one favicon still counts */
    const clean = Object.fromEntries(
      Object.entries(embed).filter(([, v]) => v && String(v).trim()),
    ) as EmbedData;
    if (!Object.keys(clean).length) return;
    setMsgs((ms) => [
      ...ms,
      {
        id: `m${Date.now()}`,
        author: "you",
        t: "just now",
        text: "",
        embed: clean,
      },
    ]);
    setEmbed({});
    setEmbedMode(false);
  }

  function grow() {
    const el = textarea.current;
    if (!el) return;
    const max = Math.round(window.innerHeight / 2);
    const from = el.clientHeight;
    /* scrollHeight can't drop below the set height, so measure with the bar
       collapsed — but with transitions off, then restore + reflow so the
       measurement never paints and the real change animates px → px.
       overflow is hidden for the animation — otherwise the textarea's
       scrollbar flashes for a frame each time the height changes. */
    el.style.transition = "none";
    el.style.overflowY = "hidden";
    el.style.height = "0px";
    const to = Math.min(el.scrollHeight, max);
    el.style.height = `${from}px`;
    void el.offsetHeight; // commit the restore before re-enabling the transition
    el.style.transition = "";
    el.style.height = `${to}px`;
    setTaH(to); // drives the side controls' slide-down offset
    const restore = () => {
      el.style.overflowY = "";
      el.removeEventListener("transitionend", restore);
    };
    el.addEventListener("transitionend", restore);
    setTimeout(restore, 250); // fallback if the transition gets interrupted
  }

  /* voice message recording — mic → MediaRecorder; stop stages the blob
     as a voice-message chip, discard just drops it */
  const [recOn, setRecOn] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const recStream = useRef<MediaStream | null>(null);
  const recChunks = useRef<Blob[]>([]);
  const recTimer = useRef(0);

  async function startRecording() {
    /* mediaDevices only exists in secure contexts (https/localhost) —
       over plain http the api is missing entirely, not just denied */
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream);
      recStream.current = stream;
      rec.current = r;
      recChunks.current = [];
      r.ondataavailable = (e) => {
        if (e.data.size) recChunks.current.push(e.data);
      };
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (!recChunks.current.length) return;
        const blob = new Blob(recChunks.current, {
          type: r.mimeType || "audio/webm",
        });
        /* src stays on the chip so the sent message renders a real
           <audio> player — the stage strip shows a mic chip for audio */
        setPending((p) => [
          ...p,
          {
            name: "voice-message.webm",
            src: URL.createObjectURL(blob),
            kind: "audio",
          },
        ]);
      };
      r.start();
      setRecSecs(0);
      setRecOn(true);
      recTimer.current = window.setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch {
      /* mic denied — nothing to record */
    }
  }

  function endRecording(keep: boolean) {
    const r = rec.current;
    window.clearInterval(recTimer.current);
    setRecOn(false);
    rec.current = null;
    if (!r) return;
    if (!keep) {
      /* discard — keep onstop only for track cleanup */
      recChunks.current = [];
      r.ondataavailable = null;
      r.onstop = () =>
        recStream.current?.getTracks().forEach((t) => t.stop());
    }
    r.stop();
  }


  /* inserts an emoji at the caret — picker stays open so people can
     chain; caret lands just after the inserted char */
  function insertEmoji(char: string) {
    const el = textarea.current;
    const from = el?.selectionStart ?? draft.length;
    const to = el?.selectionEnd ?? from;
    setDraft(draft.slice(0, from) + char + draft.slice(to));
    const at = from + char.length;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at, at);
      grow();
    });
  }

  /* spell.check — debounced miss scan for the spell mirror */
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!draft.trim()) {
        setMisses([]);
        return;
      }
      try {
        const r = await fetch("/sp/spellcheck/check", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ text: draft }),
        });
        const j = (await r.json()) as { misses?: typeof misses };
        /* drop stale responses — the draft may have moved on */
        if (textarea.current?.value === draft) setMisses(j.misses ?? []);
      } catch {
        setMisses([]);
      }
    }, draft.trim() ? 300 : 0);
    return () => clearTimeout(t);
  }, [draft]);

  async function submit() {
    if (overCap) {
      menu.confirm({
        title: "Message too long",
        body: `This message is over the ${CAP.toLocaleString()} character limit. Trim it down to send it.`,
        danger: true,
        actions: [{ label: "Got it", tone: "accent" }],
      });
      return;
    }
    let text = draft.trim();
    /* one last pass — fixes anything typed right up to the send */
    try {
      const r = await fetch("/sp/spellcheck/correct", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const j = (await r.json()) as { text?: string };
      if (j.text) text = j.text;
    } catch {
      /* offline service — send the raw draft */
    }
    if (!text && !pending.length) return;
    if (editing) {
      /* save the edit — stamps the (edited) tag */
      setMsgs((ms) =>
        ms.map((m) => (m.id === editing.id ? { ...m, text, edited: true } : m)),
      );
      setEditing(null);
    } else {
      setMsgs((ms) => [
        ...ms,
        {
          id: `m${Date.now()}`,
          author: "you",
          t: "just now",
          text,
          replyTo: replyTo?.id,
          attachments: pending.length
            ? pending.map((f) => ({ name: f.name, src: f.src }))
            : undefined,
        },
      ]);
      setPending([]);
    }
    setDraft("");
    setReplyTo(null);
    const el = textarea.current;
    if (el) {
      el.style.height = "0px";
      setTaH(36);
    }
  }

  /* every keystroke re-renders this whole frame for the draft — so the
     rendered rows are memoized: identical element references let React
     bail out of the entire list subtree instead of re-running every
     message's mdRender per key */
  const msgRows = useMemo(
    () =>
      msgs.map((msg, i) => {
        const prev = msgs[i - 1];
        /* consecutive same-author messages hang off the one above
           — no avatar, ts shows on hover. a reply always starts
           a fresh block so the quote spine has room */
        const grouped =
          !!prev && prev.author === msg.author && !msg.replyTo;
        const a = memberOf(msg.author);
        /* shared by right-click, the ⋯ hover button, and the
           toolbar's own items */
        const acts = {
          react: (e2: string) => toggleReaction(msg.id, e2),
          reply: () => {
            setReplyTo(msg);
            textarea.current?.focus();
          },
          edit: () => {
            setEditing(msg);
            setDraft(msg.text);
            textarea.current?.focus();
          },
          del: () =>
            setMsgs((ms) => ms.filter((x) => x.id !== msg.id)),
          pin: () =>
            setMsgs((ms) =>
              ms.map((x) =>
                x.id === msg.id ? { ...x, pinned: !x.pinned } : x,
              ),
            ),
          report: () => menu.popup(reportDef("user")),
        };
        return (
          <div
            key={msg.id}
            data-mid={msg.id}
            className={`group relative rounded-md transition-colors duration-500 ${
              grouped ? "mt-0.5" : "mt-4"
            } ${flash === msg.id ? "bg-accent/10" : ""}`}
            /* right-click → the row's context menu; double-click
               replies — both are the discord reflexes */
            onContextMenu={(e) => {
              e.preventDefault();
              menu.openAt(msgDef(msg, acts), e.clientX, e.clientY);
            }}
            onDoubleClick={() => {
              setReplyTo(msg);
              textarea.current?.focus();
            }}
          >
            {/* hover actions — react, reply, edit+delete (own),
                report (theirs) */}
            <div className="absolute -top-3 right-1 z-10 hidden gap-0.5 rounded-lg bg-slab p-0.5 ring-1 ring-line shadow-lg group-hover:flex">
              <IconButton
                label="React"
                size="xs"
                tip={{ title: "Add reaction", icon: SmileIcon }}
                onClick={(e) =>
                  menu.open(
                    emojiDef((char) => toggleReaction(msg.id, char)),
                    e.currentTarget,
                  )
                }
              >
                <SmileIcon className="h-4 w-4" />
              </IconButton>
              <IconButton
                label="Reply"
                size="xs"
                tip={{ title: "Reply", icon: ReplyIcon }}
                onClick={() => {
                  setReplyTo(msg);
                  textarea.current?.focus();
                }}
              >
                <ReplyIcon className="h-4 w-4" />
              </IconButton>
              {msg.author === "you" ? (
                <>
                  <IconButton
                    label="Edit"
                    size="xs"
                    tip={{ title: "Edit", icon: PencilIcon }}
                    onClick={acts.edit}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </IconButton>
                  <IconButton
                    label="Delete"
                    size="xs"
                    tip={{ title: "Delete", icon: TrashIcon }}
                    onClick={() =>
                      menu.confirm({
                        title: "Delete message?",
                        body: "This can't be undone.",
                        danger: true,
                        actions: [
                          {
                            label: "Delete",
                            tone: "accent",
                            onClick: () =>
                              setMsgs((ms) =>
                                ms.filter((x) => x.id !== msg.id),
                              ),
                          },
                          { label: "Cancel" },
                        ],
                      })
                    }
                  >
                    <TrashIcon className="h-4 w-4 text-dnd" />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  label="Report"
                  size="xs"
                  tip={{ title: "Report", icon: FlagIcon }}
                  onClick={() => menu.popup(reportDef("user"))}
                >
                  <FlagIcon className="h-4 w-4" />
                </IconButton>
              )}
              <IconButton
                label="More"
                size="xs"
                tip={{ title: "More", icon: DotsIcon }}
                onClick={(e) =>
                  menu.openAt(msgDef(msg, acts), e.clientX, e.clientY)
                }
              >
                <DotsIcon className="h-4 w-4" />
              </IconButton>
            </div>

            {/* reply spine — the hook connector + mini author line
                quoting the original; click jumps to it */}
            {msg.replyTo ? (
              <ReplyQuote
                target={msgs.find((x) => x.id === msg.replyTo)}
                onJump={() => jumpTo(msg.replyTo!)}
              />
            ) : null}

            {grouped ? (
              <div className="flex">
                {/* the hover timestamp hangs where the avatar
                    would be — same left column width as Pfp+gap */}
                <span className="flex w-[50px] shrink-0 items-center justify-center pr-1 text-[9px] tabular-nums text-fg-faint opacity-0 transition group-hover:opacity-100">
                  {msg.t}
                </span>
                <div className="min-w-0 flex-1">
                  {msg.text ? (
                    <p className="text-body leading-snug text-fg-mute break-words">
                      {mdRender(msg.text, mention)}
                    </p>
                  ) : null}
                  <AttachmentList msg={msg} />
                  {msg.embed ? <EmbedCard e={msg.embed} /> : null}
                  <ReactionRow msg={msg} onToggle={toggleReaction} />
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  aria-label={`${a.name}'s profile`}
                  onClick={() => a.m && menu.popup(profileDef(a.m))}
                  className="mt-0.5 shrink-0 rounded-lg transition hover:brightness-110"
                >
                  <Pfp src={a.src} status={a.status} size={38} />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => a.m && menu.popup(profileDef(a.m))}
                      className="truncate rounded-sm text-heading font-semibold text-fg transition hover:text-accent-2"
                    >
                      {a.name}
                    </button>
                    {a.m ? <MemberBadges m={a.m} size={13} /> : null}
                    <span className="text-micro tabular-nums text-fg-faint">
                      {msg.t}
                      {msg.edited ? " · edited" : ""}
                    </span>
                  </p>
                  {msg.text ? (
                    <p className="mt-0.5 text-body leading-snug text-fg-mute break-words">
                      {mdRender(msg.text, mention)}
                    </p>
                  ) : null}
                  <AttachmentList msg={msg} />
                  {msg.embed ? <EmbedCard e={msg.embed} /> : null}
                  <ReactionRow msg={msg} onToggle={toggleReaction} />
                </div>
              </div>
            )}
          </div>
        );
      }),
    [msgs, flash, menu, mention, toggleReaction, jumpTo],
  );

  /* non-chat routes — dashboard and friends get their own views inside
     the same shell slot */
  if (kind === "dashboard") return <DashboardView mobile={mobile} />;
  if (kind === "friends")
    return (
      <FriendsView
        mobile={mobile}
        onToggleDetails={onToggleDetails}
        detailsOn={detailsOn}
      />
    );

  return (
    <section
      className={`flex min-w-0 flex-1 flex-col overflow-hidden bg-ink ${
        mobile ? "" : "mt-[var(--matte-t)]"
      }`}
    >
      {/* header */}
      <header
        className={`flex shrink-0 items-center gap-3 border-b border-line pl-5 py-3.5 ${
          mobile ? "pr-0" : "pr-5"
        }`}
      >
        <button
          type="button"
          /* dm: pressing the identity opens the mid profile popup;
             gc/notes still toggle the details sidebar */
          onClick={() =>
            dm ? menu.popup(profileDef(dm)) : onToggleDetails()
          }
          className="-my-1.5 -ml-2 flex min-w-0 items-center gap-3 rounded-lg py-1.5 pr-2 pl-2 transition hover:bg-raise"
        >
          {chan ? (
            /* channels get a # glyph, not a face */
            <HashIcon className="h-6 w-6 shrink-0 text-fg-faint" />
          ) : (
            <Pfp
              status={head.status}
              size={36}
              surface="bg-ink"
              src={head.src}
            />
          )}
          <span className="min-w-0 text-left">
            <span className="block truncate text-note leading-snug font-semibold text-fg">
              {head.title}
            </span>
            <span className="block truncate text-micro leading-tight text-fg-faint">
              {head.sub}
            </span>
          </span>
        </button>

        <div className="ml-auto flex items-center gap-0.5">
          <IconButton
            label="Pinned messages"
            tip={{ title: "Pins", icon: PinIcon }}
            onClick={() => menu.popup(pinsDef(msgs, jumpTo))}
          >
            <PinIcon className="h-4.5 w-4.5" />
          </IconButton>
          <IconButton
            label="Conversation info"
            tip={{ title: "Details", icon: InfoIcon }}
            onClick={() => menu.popup(menus.info)}
          >
            <InfoIcon className="h-4.5 w-4.5" />
          </IconButton>
          <IconButton
            label="Search"
            tip={{ title: "Search", icon: SearchIcon, hint: "Ctrl F" }}
            onClick={() => menu.popup(menus.search)}
          >
            <SearchIcon className="h-4.5 w-4.5" />
          </IconButton>
          <IconButton
            label="Toggle sidebar"
            aria-pressed={detailsOn}
            onClick={onToggleDetails}
            className={detailsOn ? "bg-raise text-fg" : ""}
            tip={{
              title: "Sidebar",
              icon: SidebarIcon,
              hint: "Ctrl D",
              description:
                "Show or hide the details panel for this conversation.",
            }}
          >
            <SidebarIcon className="h-4.5 w-4.5" />
          </IconButton>
        </div>
      </header>

      {/* thread */}
      <div
        ref={threadRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          setScrolledUp(el.scrollHeight - el.scrollTop - el.clientHeight > 60);
          /* near the top → pull the next page of history */
          if (el.scrollTop < 40) loadPast();
        }}
        className="min-h-0 flex-1 overflow-y-auto scroll-dark"
      >
        {/* mt-auto — short threads dock to the bottom like discord */}
        <div className="mt-auto flex flex-col px-5 pb-2">
          {/* top-of-thread states — a pull shimmer while a page loads,
              then the "start of conversation" marker once history's
              exhausted */}
          {loadingPast ? (
            <div aria-hidden className="mt-4 space-y-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-4 animate-pulse rounded bg-raise"
                  style={{ width: `${62 - i * 17}%` }}
                />
              ))}
            </div>
          ) : !hasMore ? (
            <div className="mt-6 mb-2">
              {head.src ? (
                <Pfp src={head.src} status={head.status} size={64} />
              ) : (
                <span className="grid h-16 w-16 place-items-center rounded-full bg-raise ring-1 ring-line">
                  <HashIcon className="h-8 w-8 text-fg-mute" />
                </span>
              )}
              <p className="mt-3 text-xl font-bold text-fg">
                {chan ? `Welcome to #${chan.name}!` : head.title}
              </p>
              <p className="mt-1 text-note text-fg-faint">
                {chan
                  ? `This is the start of the #${chan.name} channel.`
                  : dm
                    ? `This is the beginning of your direct message history with ${head.title}.`
                    : `This is the very beginning of ${head.title}.`}
              </p>
            </div>
          ) : null}
          {/* the day's divider — v1 stamps everything "Today" */}
          <div className="mt-4 mb-1 flex items-center gap-3">
            <span className="h-px flex-1 bg-line" />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-fg-faint">
              Today
            </span>
            <span className="h-px flex-1 bg-line" />
          </div>
          {msgs.length === 0 ? (
            <p className="self-center py-16 text-note text-fg-mute">
              No messages yet — say hi.
            </p>
          ) : (
            msgRows
          )}
        </div>
      </div>

      {/* jump to latest — floats over the composer's top edge when
          you're scrolled up; jumps straight to the newest line */}
      {scrolledUp ? (
        <button
          type="button"
          onClick={() => {
            const el = threadRef.current;
            if (el) {
              el.scrollTop = el.scrollHeight;
              setScrolledUp(false);
            }
          }}
          className="absolute bottom-2 right-4 z-20 flex items-center gap-1 rounded-full bg-accent px-3 py-1.5 text-micro font-semibold text-fg shadow-lg transition hover:brightness-110 active:scale-95 animate-pop"
        >
          Jump to latest ↓
        </button>
      ) : null}

      {/* composer — glued to the bottom + walls on mobile, floating card on
          desktop; send button is mobile-only (Enter sends on desktop) */}
      <div className={`relative shrink-0 ${mobile ? "" : "px-5 pt-2 pb-4"}`}>
        {/* :shortcode: autocomplete — floats above the composer,
            arrows+tab/enter accept, esc dismisses */}
        {ac ? (
          <div className="absolute inset-x-0 bottom-full z-20 mb-1 overflow-hidden rounded-xl bg-slab ring-1 ring-line shadow-xl animate-pop">
            {ac.list.map((r, i) => (
              <button
                key={r.c}
                type="button"
                onMouseEnter={() => setAc({ ...ac, sel: i })}
                onClick={() => acceptShortcode(r)}
                className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition ${
                  i === ac.sel ? "bg-raise" : ""
                }`}
              >
                <Emoji char={r.e} size={20} />
                <span className="truncate text-note text-fg">
                  :{r.n.replace(/ /g, "_")}:
                </span>
              </button>
            ))}
          </div>
        ) : null}
        {/* editing chip — mirrors the reply chip, amber accent */}
        {editing ? (
          <div className="mb-1.5 flex items-center gap-2 rounded-lg bg-field px-2.5 py-1.5 ring-1 ring-line">
            <PencilIcon className="h-3.5 w-3.5 shrink-0 text-idle" />
            <span className="min-w-0 flex-1 truncate text-micro text-fg-mute">
              Editing — <span className="text-fg-faint">{editing.text}</span>
            </span>
            <button
              type="button"
              aria-label="Cancel edit"
              onClick={() => {
                setEditing(null);
                setDraft("");
              }}
              className="grid h-5 w-5 shrink-0 place-items-center rounded-md text-fg-faint transition hover:bg-raise hover:text-fg"
            >
              <XIcon className="h-3 w-3" />
            </button>
          </div>
        ) : null}
        {/* typing indicator — sits above the bar, discord-style */}
        {typing ? (
          <div className="mb-1 flex items-center gap-1.5 px-1 text-micro text-fg-faint">
            <span className="flex gap-0.5" aria-hidden>
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-mute" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-mute [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-mute [animation-delay:300ms]" />
            </span>
            {kind === "gc" ? "someone" : dm ? dm.name : "someone"} is typing…
          </div>
        ) : null}
        {/* reply-to chip — floats above the composer while set */}
        {replyTo ? (
          <div className="mb-1.5 flex items-center gap-2 rounded-lg bg-field px-2.5 py-1.5 ring-1 ring-line">
            <ReplyIcon className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
            <span className="min-w-0 flex-1 truncate text-micro text-fg-mute">
              Replying to{" "}
              <span className="font-semibold text-fg">
                {memberOf(replyTo.author).name}
              </span>
              <span className="text-fg-faint"> — {replyTo.text}</span>
            </span>
            <button
              type="button"
              aria-label="Cancel reply"
              onClick={() => setReplyTo(null)}
              className="grid h-5 w-5 shrink-0 place-items-center rounded-md text-fg-faint transition hover:bg-raise hover:text-fg"
            >
              <XIcon className="h-3 w-3" />
            </button>
          </div>
        ) : null}
        {/* staged uploads — image thumbs or file chips with an ✕ to
            unstage; they attach to the next send */}
        {pending.length ? (
          <div className="mb-1.5 flex flex-wrap gap-2 px-1">
            {pending.map((f, i) => (
              <span key={`${f.name}-${i}`} className="group/up relative">
                {(() => {
                  const k = attachKind(f);
                  return f.src && k === "image" ? (
                    // eslint-disable-next-line @next/next/no-img-element -- local object URL
                    <img
                      src={f.src}
                      alt={f.name}
                      className="h-14 w-14 rounded-lg object-cover ring-1 ring-line"
                    />
                  ) : f.src && k === "video" ? (
                    /* first frame as the thumb — muted, no autoplay */
                    <video
                      src={f.src}
                      muted
                      playsInline
                      preload="metadata"
                      className="h-14 w-14 rounded-lg object-cover ring-1 ring-line"
                    />
                  ) : (
                    <span className="flex h-14 max-w-44 items-center gap-2 rounded-lg bg-field px-3 ring-1 ring-line">
                      {k === "audio" ? (
                        <MicIcon className="h-4 w-4 shrink-0 text-accent" />
                      ) : (
                        <FileIcon className="h-4 w-4 shrink-0 text-fg-faint" />
                      )}
                      <span className="truncate text-micro text-fg-mute">
                        {f.name}
                      </span>
                    </span>
                  );
                })()}
                <button
                  type="button"
                  aria-label={`Remove ${f.name}`}
                  onClick={() =>
                    setPending((p) => p.filter((_, x) => x !== i))
                  }
                  className="absolute -top-1.5 -right-1.5 grid h-4.5 w-4.5 place-items-center rounded-full bg-slab text-fg-faint ring-1 ring-line transition hover:text-fg"
                >
                  <XIcon className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        {/* character counter — floats above the bar, not inside it */}
        {draft.length >= CAP ? (
          <span
            className={`absolute right-1.5 bottom-full mb-1.5 text-micro tabular-nums ${
              overCap ? "font-semibold text-idle" : "text-fg-mute"
            }`}
          >
            {draft.length.toLocaleString()}/{CAP.toLocaleString()}
          </span>
        ) : null}

        {/* the slide window — hides whichever bar is off; its height
            tweens between the two bars' measured heights so the swap
            pushes the thread by exactly the delta */}
        <div
          className="overflow-hidden transition-[height] duration-250 ease-out"
          style={{
            height: barH.co ? (embedMode ? barH.ed : barH.co) : undefined,
          }}
        >
          <div
            className="transition-transform duration-250 ease-out"
            style={{
              transform: `translateY(${embedMode ? -barH.co : 0}px)`,
            }}
          >
          <div ref={coBar}>
        <div
          className={`flex items-center gap-1.5 rounded-t-2xl bg-field px-2.5 py-2 ring-1 ring-line transition ${
            mobile ? "rounded-b-[5px]" : ""
          }`}
        >
          <span
            className="shrink-0 transition-transform duration-200"
            style={drop ? { transform: `translateY(${drop}px)` } : undefined}
          >
            <input
              ref={fileInput}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                /* images get an object-URL thumbnail so the stage strip
                   and the sent message show real previews */
                setPending((p) => [
                  ...p,
                  ...files.map((f) => ({
                    name: f.name,
                    src: f.type.startsWith("image/")
                      ? URL.createObjectURL(f)
                      : undefined,
                  })),
                ]);
                e.target.value = ""; // same file twice still re-fires
              }}
            />
            <IconButton
              label="Attach"
              size="lg"
              tip={{ title: "Attach", icon: PlusIcon }}
              onClick={(e) => {
                const def = attachDef(
                  () => fileInput.current?.click(),
                  () => void startRecording(),
                  () =>
                    menu.popup(
                      cameraDef((name, src, kind) =>
                        setPending((p) => [...p, { name, src, kind }]),
                      ),
                    ),
                );
                /* mobile wants the bottom pullup, desktop the docked card */
                if (mobile) menu.popup(def);
                else menu.open(def, e.currentTarget);
              }}
            >
              <PlusIcon className="h-5.5 w-5.5" />
            </IconButton>
          </span>

          <label htmlFor="composer" className="sr-only">
            Message
          </label>
          {recOn ? (
            /* recording replaces the input — red live dot + timer,
               discard or stage */
            <div className="flex min-w-0 flex-1 items-center gap-2.5 py-2">
              <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-dnd" />
              <span className="text-body text-fg">
                Recording…{" "}
                <span className="tabular-nums text-fg-mute">
                  {Math.floor(recSecs / 60)}:
                  {String(recSecs % 60).padStart(2, "0")}
                </span>
              </span>
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                <IconButton
                  label="Discard"
                  size="lg"
                  tip={{ title: "Discard", icon: XIcon }}
                  onClick={() => endRecording(false)}
                >
                  <XIcon className="h-5 w-5" />
                </IconButton>
                <IconButton
                  label="Keep"
                  size="lg"
                  tip={{ title: "Stage voice message", icon: CheckIcon }}
                  onClick={() => endRecording(true)}
                >
                  <CheckIcon className="h-5 w-5" />
                </IconButton>
              </span>
            </div>
          ) : (
          <>
          <div className="relative min-w-0 flex-1">
          {/* spell mirror — identical font/leading/padding/wrap so word
              offsets line up; only the decorations are visible
              (text-transparent), and only marked words take pointer input */}
          <div
            ref={spellMirror}
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10 overflow-hidden whitespace-pre-wrap py-2 text-body leading-6 text-transparent [overflow-wrap:anywhere]"
          >
            {(() => {
              const segs: React.ReactNode[] = [];
              let cur = 0;
              misses.forEach((m, i) => {
                if (m.off > cur) segs.push(draft.slice(cur, m.off));
                segs.push(
                  <span
                    key={i}
                    className={`spell-miss pointer-events-auto cursor-text ${
                      m.fix ? "spell-fix" : ""
                    }`}
                    onPointerEnter={(e) =>
                      showTip(
                        {
                          title: m.sug.length
                            ? m.sug.join(" · ")
                            : "No suggestions",
                          hint: m.fix ? "send" : undefined,
                          description: m.fix
                            ? `Autocorrects to "${m.fix}" when you send`
                            : undefined,
                        },
                        e.currentTarget,
                      )
                    }
                    onPointerLeave={hideTip}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      hideTip();
                      const el = textarea.current;
                      el?.focus();
                      el?.setSelectionRange(m.off, m.off + m.len);
                    }}
                  >
                    {draft.slice(m.off, m.off + m.len)}
                  </span>,
                );
                cur = m.off + m.len;
              });
              segs.push(draft.slice(cur) + "\n");
              return segs;
            })()}
          </div>
          <textarea
            id="composer"
            ref={textarea}
            rows={1}
            value={draft}
            onScroll={(e) => {
              /* keep the mirror pinned to the textarea's scroll pos */
              const m = spellMirror.current;
              if (m) m.scrollTop = e.currentTarget.scrollTop;
            }}
            onChange={(e) => {
              setDraft(e.target.value);
              grow();
              checkShortcode(e.target.value, e.target.selectionStart);
            }}
            onSelect={(e) =>
              checkShortcode(e.currentTarget.value, e.currentTarget.selectionStart)
            }
            onKeyDown={(e) => {
              if (ac) {
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  setAc({
                    ...ac,
                    sel:
                      (ac.sel + (e.key === "ArrowDown" ? 1 : -1) + ac.list.length) %
                      ac.list.length,
                  });
                  return;
                }
                if (e.key === "Enter" || e.key === "Tab") {
                  e.preventDefault();
                  acceptShortcode(ac.list[ac.sel]);
                  return;
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setAc(null);
                  return;
                }
              }
              if (e.key === "ArrowUp" && !draft.trim()) {
                /* the discord reflex — up on an empty box edits your
                   most recent message */
                const last = [...msgs].reverse().find((m) => m.author === "you");
                if (last) {
                  e.preventDefault();
                  setEditing(last);
                  setDraft(last.text);
                }
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setEditing(null);
                setReplyTo(null);
                e.currentTarget.blur();
                return;
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={`Message ${
              dm ? dm.name : chan ? `#${chan.name}` : MESSAGE_TO[kind]
            }`}
            autoCorrect="off"
            spellCheck={false}
            className="relative max-h-[50dvh] min-h-9 w-full resize-none overflow-y-auto bg-transparent py-2 text-body leading-6 text-fg outline-none transition-[height] duration-150 ease-out placeholder:text-fg-faint scroll-none"
          />
          </div>

          {/* trailing controls — vertically centered while the bar is
              single-line, slide to the bottom once the textarea grows
              a second line */}
          <span
            className="flex shrink-0 items-center gap-1.5 transition-transform duration-200"
            style={drop ? { transform: `translateY(${drop}px)` } : undefined}
          >
            <IconButton
              label="Emoji"
              size={mobile ? "sm" : "lg"}
              tip={{ title: "Emoji", icon: SmileIcon }}
              /* mobile docks the picker under the composer instead of
                 popping a card — one button, the panel's own tab pills
                 switch emoji/sticker/gif */
              onClick={(e) =>
                mobile
                  ? setMPicker((o) => !o)
                  : menu.open(emojiDef(insertEmoji), e.currentTarget)
              }
            >
              {/* mobile swaps to a keyboard glyph while the panel's up —
                  the tap reads as "back to typing" */}
              {mobile && mPicker ? (
                <KeyboardIcon className="h-5.5 w-5.5" />
              ) : (
                <SmileIcon className="h-5.5 w-5.5" />
              )}
            </IconButton>
            {mobile ? null : (
              <>
                <IconButton
                  label="Stickers"
                  size="lg"
                  tip={{ title: "Stickers", icon: StickerIcon }}
                  onClick={(e) =>
                    menu.open(emojiDef(insertEmoji, "stickers"), e.currentTarget)
                  }
                >
                  <StickerIcon className="h-5.5 w-5.5" />
                </IconButton>
                <IconButton
                  label="GIFs"
                  size="lg"
                  tip={{ title: "GIFs", icon: GifIcon }}
                  onClick={(e) =>
                    menu.open(emojiDef(insertEmoji, "gifs"), e.currentTarget)
                  }
                >
                  <GifIcon className="h-5.5 w-5.5" />
                </IconButton>
              </>
            )}

            {mobile ? (
              <span
                aria-hidden
                className="mx-0.5 h-5 w-px shrink-0 self-center bg-line"
              />
            ) : null}

            {/* always-on send — gray well until there's a draft, then it
                fills blue. the gift/embed triggers are hidden for now,
                their code stays around for when the slot comes back */}
            <button
              type="button"
              disabled={!draft.trim() && !pending.length}
              onClick={submit}
              onPointerEnter={(e) =>
                showTip(
                  { title: "Send", icon: SendIcon, hint: "Enter" },
                  e.currentTarget,
                )
              }
              onPointerLeave={hideTip}
              aria-label="Send message"
              className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl transition duration-200 active:scale-90 disabled:cursor-not-allowed ${
                draft.trim() || pending.length
                  ? "bg-accent text-fg hover:bg-accent/90"
                  : "bg-raise text-fg-faint"
              }`}
            >
              <SendIcon className="h-5.5 w-5.5" />
            </button>
          </span>
          </>
          )}
        </div>
          </div>
          {/* the embed builder — a quarter-height form that slides up
              in the composer's place; three collapsible segments
              (header / body / footer), sends a real embed card */}
          <div ref={edBar} className="pt-1.5">
            <div
              className={`rounded-t-2xl bg-field ring-1 ring-line transition ${
                mobile ? "rounded-b-[5px]" : ""
              }`}
            >
              {/* builder top rail — back + label + send */}
              <div className="flex items-center gap-1.5 px-2.5 pt-2">
                <IconButton
                  label="Back to message"
                  size={mobile ? "sm" : "lg"}
                  tip={{ title: "Back to message", icon: ArrowLeftIcon }}
                  onClick={() => setEmbedMode(false)}
                >
                  <ArrowLeftIcon className="h-5.5 w-5.5" />
                </IconButton>
                <span className="flex flex-1 items-center gap-1.5 text-micro font-semibold uppercase tracking-wider text-fg-faint">
                  <EmbedIcon className="h-4 w-4" />
                  Embed builder
                </span>
                <button
                  type="button"
                  onClick={submitEmbed}
                  onPointerEnter={(e) =>
                    showTip(
                      { title: "Send embed", icon: SendIcon },
                      e.currentTarget,
                    )
                  }
                  onPointerLeave={hideTip}
                  aria-label="Send embed"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent text-fg transition duration-150 hover:bg-accent/90 active:scale-90"
                >
                  <SendIcon className="h-5.5 w-5.5" />
                </button>
              </div>
              {/* the form — caps at ~a quarter of the frame, scrolls
                  past that */}
              <div className="max-h-[25dvh] overflow-y-auto px-2.5 pb-2 scroll-dark">
                <EmbedSeg
                  title="Header"
                  open={seg.header}
                  onToggle={() =>
                    setSeg((s) => ({ ...s, header: !s.header }))
                  }
                >
                  <EmbedField
                    label="Sub header"
                    value={embed.subHeader}
                    onChange={(v) =>
                      setEmbed((e) => ({ ...e, subHeader: v }))
                    }
                  />
                  <EmbedField
                    label="Sub favicon"
                    value={embed.subIcon}
                    placeholder="icon url"
                    onChange={(v) =>
                      setEmbed((e) => ({ ...e, subIcon: v }))
                    }
                  />
                  <EmbedField
                    label="Header"
                    value={embed.header}
                    onChange={(v) =>
                      setEmbed((e) => ({ ...e, header: v }))
                    }
                  />
                  <EmbedField
                    label="Favicon"
                    value={embed.icon}
                    placeholder="icon url"
                    onChange={(v) =>
                      setEmbed((e) => ({ ...e, icon: v }))
                    }
                  />
                  {/* tone — the card's accent bar color */}
                  <span className="flex items-center gap-2 py-0.5">
                    <span className="w-20 shrink-0 truncate text-micro text-fg-mute">
                      Tone
                    </span>
                    <input
                      type="color"
                      aria-label="Embed tone"
                      value={embed.tone || "#5865f2"}
                      onChange={(e) =>
                        setEmbed((em) => ({ ...em, tone: e.target.value }))
                      }
                      className="h-6 w-10 cursor-pointer rounded bg-transparent"
                    />
                    {embed.tone ? (
                      <button
                        type="button"
                        onClick={() =>
                          setEmbed((em) => ({ ...em, tone: undefined }))
                        }
                        className="text-micro text-fg-faint transition hover:text-fg"
                      >
                        clear
                      </button>
                    ) : (
                      <span className="text-micro text-fg-faint">
                        default
                      </span>
                    )}
                  </span>
                </EmbedSeg>
                <EmbedSeg
                  title="Body"
                  open={seg.body}
                  onToggle={() =>
                    setSeg((s) => ({ ...s, body: !s.body }))
                  }
                >
                  <EmbedField
                    label="Text"
                    value={embed.body}
                    onChange={(v) =>
                      setEmbed((e) => ({ ...e, body: v }))
                    }
                  />
                  <EmbedField
                    label="Favicon"
                    value={embed.bodyIcon}
                    placeholder="icon url"
                    onChange={(v) =>
                      setEmbed((e) => ({ ...e, bodyIcon: v }))
                    }
                  />
                </EmbedSeg>
                <EmbedSeg
                  title="Footer"
                  open={seg.footer}
                  onToggle={() =>
                    setSeg((s) => ({ ...s, footer: !s.footer }))
                  }
                >
                  <EmbedField
                    label="Footer"
                    value={embed.footer}
                    onChange={(v) =>
                      setEmbed((e) => ({ ...e, footer: v }))
                    }
                  />
                  <EmbedField
                    label="Favicon"
                    value={embed.footerIcon}
                    placeholder="icon url"
                    onChange={(v) =>
                      setEmbed((e) => ({ ...e, footerIcon: v }))
                    }
                  />
                </EmbedSeg>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* mobile media panel — docks under the composer like a keyboard:
          opening it pushes the composer up and shrinks the thread in
          place (no popup, no sheet). stays mounted so the grid-rows
          collapse animates both ways — gif fetches only run when a gif
          tab is actually open, so the cost of staying mounted is nil.
          the picker's own pills switch emoji/sticker/gif */}
      {mobile ? (
        <div
          className={`grid shrink-0 transition-[grid-template-rows] duration-300 ease-in-out ${
            mPicker ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={`px-3 pb-3 pt-1 ${mPicker ? "border-t border-line" : ""}`}
            >
              <EmojiPicker mobile onPick={insertEmoji} />
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/* reaction chips under a message — discord-style: emoji + count,
   accent ring when the local account reacted, click toggles */
function ReactionRow({
  msg,
  onToggle,
}: {
  msg: ChatMsg;
  onToggle: (id: string, e: string) => void;
}) {
  if (!msg.reactions?.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {msg.reactions.map((r) => (
        <button
          key={r.e}
          type="button"
          onClick={() => onToggle(msg.id, r.e)}
          className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 text-micro transition active:scale-95 ${
            r.me
              ? "bg-accent/20 text-fg ring-1 ring-accent/50"
              : "bg-raise text-fg-mute ring-1 ring-line hover:bg-lift hover:text-fg"
          }`}
        >
          <Emoji char={r.e} size={14} />
          <span className="font-semibold tabular-nums">{r.n}</span>
        </button>
      ))}
    </div>
  );
}

/* the attach card — docks at the composer's left edge (spot "cl");
   MenuItem closes the menu first, then the action runs */
const attachDef = (
  onUpload: () => void,
  onAudio: () => void,
  onPicture: () => void,
): MenuDef => ({
  id: "attach",
  title: "",
  bare: true,
  Content: () => (
    <>
      <MenuItem icon="clip" onClick={onUpload}>
        Upload a file
      </MenuItem>
      <MenuItem icon="mic" onClick={onAudio}>
        Send audio message
      </MenuItem>
      <MenuItem icon="camera" onClick={onPicture}>
        Send picture
      </MenuItem>
    </>
  ),
  spot: "cl",
  /* mobile renders it as the edge-to-edge pullup — mobileCard swaps the
     docked card for the overlay, sheetCard makes it flush-bottom */
  mobileCard: true,
  sheetCard: true,
});

/* camera popup — ui shell only for now: full-bleed viewfinder, photo/video
   mode toggle, labeled action buttons (snap pic / start·pause·end
   recording), facing flip. the device plumbing (getUserMedia +
   MediaRecorder) is cut — buttons drive the visible state so the layout
   can be reviewed, nothing stages */
const cameraDef = (
  onShot: (name: string, src: string, kind: AttachKind) => void,
): MenuDef => ({
  id: "camera",
  title: "Send picture",
  /* bare — the card supplies its own chrome so the preview can fill the
     whole 960px frame */
  bare: true,
  Content: () => <CameraCard onShot={onShot} />,
  popupSize: "lg",
  mobileCard: true,
});

function CameraCard({
  onShot: _onShot,
}: {
  onShot: (name: string, src: string, kind: AttachKind) => void;
}) {
  const menu = useMenu();
  const timer = useRef(0);
  const [mode, setMode] = useState<"photo" | "video">("photo");
  const [rec, setRec] = useState<"idle" | "rec" | "paused">("idle");
  const [secs, setSecs] = useState(0);
  const [facing, setFacing] = useState<"user" | "environment">("user");

  const tick = () => {
    timer.current = window.setInterval(() => setSecs((s) => s + 1), 1000);
  };
  const untick = () => window.clearInterval(timer.current);
  useEffect(() => untick, []);

  const fmt = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  /* no device attached — the actions just drive the ui state for now */
  const snapPhoto = () => menu.close();
  const startVid = () => {
    setSecs(0);
    setRec("rec");
    tick();
  };
  const pauseVid = () => {
    if (rec === "rec") {
      untick();
      setRec("paused");
    } else {
      setRec("rec");
      tick();
    }
  };
  const endVid = () => {
    untick();
    setRec("idle");
    menu.close();
  };
  const close = () => {
    untick();
    menu.close();
  };

  const actionCls =
    "whitespace-nowrap rounded-lg px-3 py-2 text-note font-semibold transition active:scale-[.98] disabled:opacity-40 sm:px-4";

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      {/* viewfinder — camera stream wires in here later. min-h keeps the
          frame from collapsing when the card is auto-height (mobile sheet) */}
      <div className="relative min-h-[240px] flex-1 overflow-hidden rounded-xl bg-black ring-1 ring-line">
        <div className="grid h-full place-items-center">
          <CameraIcon className="h-10 w-10 text-fg-mute/40" />
        </div>
        {rec !== "idle" ? (
          <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-shell/80 px-2.5 py-1 text-note font-medium text-fg">
            <span
              className={`h-2 w-2 rounded-full bg-dnd ${
                rec === "rec" ? "animate-pulse" : "opacity-40"
              }`}
            />
            {rec === "paused" ? "Paused · " : ""}
            {fmt(secs)}
          </span>
        ) : null}
        <button
          type="button"
          aria-label="Close camera"
          onClick={close}
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-shell/80 text-fg-dim transition hover:text-fg"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      {/* controls — mode pill left, action buttons centered, flip right */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center">
        <span className="flex w-fit rounded-lg bg-field p-0.5">
          {(["photo", "video"] as const).map((m) => (
            <button
              key={m}
              type="button"
              disabled={rec !== "idle"}
              onClick={() => setMode(m)}
              className={`rounded-md px-3 py-1.5 text-note font-medium capitalize transition ${
                mode === m
                  ? "bg-raise text-fg"
                  : "text-fg-mute hover:text-fg-dim"
              } disabled:opacity-40`}
            >
              {m}
            </button>
          ))}
        </span>
        <span className="flex items-center justify-center gap-2">
          {mode === "photo" ? (
            <button
              type="button"
              onClick={snapPhoto}
              className={`${actionCls} bg-accent text-white hover:brightness-110`}
            >
              Snap pic!
            </button>
          ) : rec === "idle" ? (
            <button
              type="button"
              onClick={startVid}
              className={`${actionCls} bg-accent text-white hover:brightness-110`}
            >
              Start recording
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={pauseVid}
                className={`${actionCls} bg-raise text-fg hover:bg-hov`}
              >
                {rec === "rec" ? "Pause" : "Resume"}
              </button>
              <button
                type="button"
                onClick={endVid}
                className={`${actionCls} bg-dnd text-white hover:brightness-110`}
              >
                End
              </button>
            </>
          )}
        </span>
        <span className="justify-self-end">
          <IconButton
            label="Flip camera"
            size="sm"
            tip={{ title: "Flip camera", icon: RefreshIcon }}
            onClick={() =>
              setFacing((f) => (f === "user" ? "environment" : "user"))
            }
          >
            <RefreshIcon className="h-4.5 w-4.5" />
          </IconButton>
        </span>
      </div>
    </div>
  );
}

/* collapsible segment of the embed builder — header row toggles, body
   height-animates via grid-rows 0fr→1fr */
function EmbedSeg({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-1.5 overflow-hidden rounded-lg ring-1 ring-line">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 bg-raise/60 px-2.5 py-1.5 text-left transition hover:bg-raise"
      >
        <ChevronDownIcon
          className={`h-3 w-3 shrink-0 text-fg-faint transition duration-200 ${
            open ? "" : "-rotate-90"
          }`}
        />
        <span className="text-micro font-semibold uppercase tracking-wider text-fg-mute">
          {title}
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-col gap-0.5 px-2.5 py-1.5">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/* one labeled field row inside a builder segment */
function EmbedField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex items-center gap-2 py-0.5">
      <span className="w-20 shrink-0 truncate text-micro text-fg-mute">
        {label}
      </span>
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        className="min-w-0 flex-1 rounded-md bg-ink/60 px-2 py-1 text-micro text-fg outline-none ring-1 ring-transparent transition placeholder:text-fg-faint focus:ring-accent/60"
      />
    </label>
  );
}

/* the sent card — discord-embed anatomy: tone bar on the left, then
   sub-header / header / body / footer, each icon optional */
function EmbedCard({ e }: { e: EmbedData }) {
  return (
    <div className="mt-1.5 flex max-w-md overflow-hidden rounded-lg bg-slab ring-1 ring-line">
      <span
        aria-hidden
        className="w-1 shrink-0"
        style={{ background: e.tone || "var(--color-accent)" }}
      />
      <div className="min-w-0 flex-1 px-3 py-2.5">
        {e.subHeader || e.subIcon ? (
          <p className="flex items-center gap-1.5 text-micro text-fg-faint">
            {e.subIcon ? (
              // eslint-disable-next-line @next/next/no-img-element -- user-supplied icon url
              <img
                src={e.subIcon}
                alt=""
                className="h-3.5 w-3.5 shrink-0 rounded-sm object-cover"
              />
            ) : null}
            <span className="truncate">{e.subHeader}</span>
          </p>
        ) : null}
        {e.header || e.icon ? (
          <p className="mt-0.5 flex items-center gap-1.5 text-title font-semibold text-fg">
            {e.icon ? (
              // eslint-disable-next-line @next/next/no-img-element -- user-supplied icon url
              <img
                src={e.icon}
                alt=""
                className="h-4.5 w-4.5 shrink-0 rounded-sm object-cover"
              />
            ) : null}
            <span className="truncate">{e.header}</span>
          </p>
        ) : null}
        {e.body || e.bodyIcon ? (
          <p className="mt-1 flex items-start gap-1.5 text-note text-fg-dim">
            {e.bodyIcon ? (
              // eslint-disable-next-line @next/next/no-img-element -- user-supplied icon url
              <img
                src={e.bodyIcon}
                alt=""
                className="mt-0.5 h-4 w-4 shrink-0 rounded-sm object-cover"
              />
            ) : null}
            <span className="min-w-0 whitespace-pre-wrap break-words">
              {e.body}
            </span>
          </p>
        ) : null}
        {e.footer || e.footerIcon ? (
          <p className="mt-1.5 flex items-center gap-1.5 text-micro text-fg-faint">
            {e.footerIcon ? (
              // eslint-disable-next-line @next/next/no-img-element -- user-supplied icon url
              <img
                src={e.footerIcon}
                alt=""
                className="h-3.5 w-3.5 shrink-0 rounded-sm object-cover"
              />
            ) : null}
            <span className="truncate">{e.footer}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/* the quote spine above a reply — hook connector curving into the
   mini avatar line, quoting the original's text. click scrolls the
   thread back to it; a deleted source shows the discord fallback */
function ReplyQuote({
  target,
  onJump,
}: {
  target: ChatMsg | undefined;
  onJump: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onJump}
      className="group/reply mb-0.5 flex w-full items-end gap-1.5 text-left"
    >
      {/* the hook — vertical stroke lands on the avatar's center axis,
          horizontal leg curls into the quote line */}
      <span
        aria-hidden
        className="ml-[9px] h-3 w-[15px] shrink-0 self-start translate-y-[7px] rounded-tl-md border-l-2 border-t-2 border-line"
      />
      {target ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element -- mini quote avatar */}
          <img
            src={memberOf(target.author).src}
            alt=""
            className="h-4 w-4 shrink-0 self-center rounded-full"
          />
          <span className="shrink-0 self-center text-note font-semibold text-fg-mute">
            {memberOf(target.author).name}
          </span>
          <span className="min-w-0 flex-1 self-center truncate text-note text-fg-faint transition group-hover/reply:text-fg-mute">
            {target.text}
          </span>
        </>
      ) : (
        <span className="min-w-0 flex-1 self-center text-note italic text-fg-faint">
          Original message was deleted
        </span>
      )}
    </button>
  );
}


/* message attachments — images render as in-thread previews (local
   object URLs until the real upload path lands), other files as
   icon cards */
function AttachmentList({ msg }: { msg: ChatMsg }) {
  if (!msg.attachments?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-2">
      {msg.attachments.map((at, i) => {
        const k = attachKind(at);
        return at.src && k === "image" ? (
          // eslint-disable-next-line @next/next/no-img-element -- local object URL
          <img
            key={`${at.name}-${i}`}
            src={at.src}
            alt={at.name}
            className="max-h-52 max-w-full rounded-lg object-cover ring-1 ring-line"
          />
        ) : at.src && k === "video" ? (
          /* camera clips play inline — blob urls live for the session */
          <video
            key={`${at.name}-${i}`}
            controls
            playsInline
            src={at.src}
            className="max-h-52 max-w-full rounded-lg ring-1 ring-line"
          />
        ) : at.src && k === "audio" ? (
          /* voice messages play inline — the blob url is live while the
             app session is (local uploads, no remote store yet) */
          <audio
            key={`${at.name}-${i}`}
            controls
            src={at.src}
            className="h-9 w-64"
          />
        ) : (
          <span
            key={`${at.name}-${i}`}
            className="flex max-w-56 items-center gap-2 rounded-lg bg-raise/70 px-3 py-2 ring-1 ring-line"
          >
            <FileIcon className="h-4 w-4 shrink-0 text-fg-faint" />
            <span className="truncate text-note text-fg-mute">
              {at.name}
            </span>
          </span>
        );
      })}
    </div>
  );
}


/* the header's pins popup — every pinned message in the thread, click
   jumps the scroll to it */
function pinsDef(
  msgs: ChatMsg[],
  jump: (id: string) => void,
): MenuDef {
  const pins = msgs.filter((m) => m.pinned);
  return {
    id: "pins",
    title: "Pinned Messages",
    tab: "Pins",
    Content: function Pins() {
      const menu = useMenu();
      if (!pins.length)
        return (
          <p className="w-64 p-4 text-note text-fg-faint">
            No pinned messages yet — right-click a message to pin it.
          </p>
        );
      return (
        <div className="w-72 py-1">
          {pins.map((m) => {
            const a = memberOf(m.author);
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  menu.close();
                  jump(m.id);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition hover:bg-raise"
              >
                {/* eslint-disable-next-line @next/next/no-img-element -- pin row avatar */}
                <img
                  src={a.src}
                  alt=""
                  className="h-6 w-6 shrink-0 rounded-full"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-note font-semibold text-fg">
                    {a.name}
                  </span>
                  <span className="block truncate text-micro text-fg-faint">
                    {m.text || `${m.attachments?.length} attachment(s)`}
                  </span>
                </span>
                <PinIcon className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
              </button>
            );
          })}
        </div>
      );
    },
  };
}


/* right-click on a message — the context menu. react opens the emoji
   picker as a centered popup (anchor gone once the menu's open); own
   messages get edit/delete, others get report */
function msgDef(
  msg: ChatMsg,
  actions: {
    react: (e: string) => void;
    reply: () => void;
    edit: () => void;
    del: () => void;
    pin: () => void;
    report: () => void;
  },
): MenuDef {
  const mine = msg.author === "you";
  return {
    id: `msg-${msg.id}`,
    title: "Message",
    /* MenuItem closes the menu itself before calling onClick, so the
       emoji popup can be opened straight from a row */
    Content: function MsgMenu() {
      const menu = useMenu();
      return (
        <>
          <MenuItem icon="smile" onClick={() => menu.popup(emojiDef(actions.react))}>
            Add reaction
          </MenuItem>
          <MenuItem icon="send" onClick={actions.reply}>
            Reply
          </MenuItem>
          <MenuItem icon="pin" onClick={actions.pin}>
            {msg.pinned ? "Unpin message" : "Pin message"}
          </MenuItem>
          <MenuItem icon="copy" onClick={() => copyText(msg.text)}>
            Copy text
          </MenuItem>
          {mine ? (
            <>
              <MenuItem icon="pencil" onClick={actions.edit}>
                Edit message
              </MenuItem>
              <MenuItem icon="trash" onClick={actions.del}>
                Delete message
              </MenuItem>
            </>
          ) : (
            <MenuItem icon="flag" onClick={actions.report}>
              Report
            </MenuItem>
          )}
        </>
      );
    },
  };
}
